
  import { computed, defineComponent, ref, watch } from 'vue';
  import { t } from '@/ui/i18n/i18n';
  import { CLICK_EVENTS, trackClick } from '@/services/activityTracker/activityTrackerHelper';
  import {
    approvalsEnabledForOrganization,
    hasFeature,
    hasPermission,
    hasSomePermission,
    inspectionApprovalsPermissions,
    isFormTranslationsFeatureEnabled,
    isOnFreePlan,
    Permissions,
    user,
  } from '@/ui/auth/auth';
  import InspectionReport from '@/ui/components/inspectionReport/InspectionReport';
  import { useTask } from '@/ui/concerns';
  import { get, listInspectionIssues } from '@/services/api/resources/inspection';
  import { Button } from '@/ui/components/Button';
  import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
  import { renderNotFoundPage } from '@/ui/pages/error/NotFoundPage';
  import { UpgradeAd } from '@/ui/components/upgrade/UpgradeAd';
  import { InspectionReportExport } from '@/ui/pages/inspection/InspectionReportExport';
  import { Index as ManageIssuesModal } from '@/ui/components/issue/ManageIssuesModal';
  import { FeatureNames, NewFeature } from '@/ui/components/NewFeature';
  import { EventBus } from '@/ui/EventBus';
  import { Events } from '@/ui/events';
  import { IssueRecord } from '@/entities/issue/v2';
  import { IssueType } from '@/entities/issue';
  import { ActivityModal } from '@/ui/components/modals/ActivityModal';
  import { FallingConfetti } from '@/ui/components/animations/FallingConfetti';
  import Level from '@/ui/components/common/Level.vue';
  import { PageTitle } from '@/ui/components/PageTitle';
  import { Box } from '@/ui/components/Box';
  import { Icon } from '@/ui/components/icon/Icon';
  import { Tag } from '@/ui/components/Tag';
  import { Loader } from '@/ui/components/Loader';
  import { Tooltip } from '@/ui/components/Tooltip';
  import { useApproval } from '@/ui/concerns/approval/useApproval';
  import { approvalStatusToText } from '@/ui/dictionary';
  import ApproveForm from '@/ui/components/approval/ApproveForm.vue';
  import { ApprovalStatus } from '@/entities/approvals/constants';
  import { reset as resetApprovalAction } from '@/services/api/resources/approval';
  import { ConfirmationModal } from '@/ui/components/modals/ConfirmationModal';
  import { legacyRecord } from '@/entities/inspection/model';
  import { Status } from '@/entities/todo';
  import SystemRejectedButton from '@/ui/components/inspection/SystemRejectedButton.vue';
  import { filterListToOptions, sequence } from '@/ui/helpers/uiHelper';
  import { filter as getAllCategories } from '@/services/api/resources/category';
  import { SelectOptions } from '@/ui/types';

  interface InspectionDetailPageProps {
    id: number;
    showConfetti: boolean;
  }

  export default defineComponent<InspectionDetailPageProps>({
    name: 'InspectionDetailPage',
    components: {
      SystemRejectedButton,
      Box,
      Tag,
      Icon,
      InspectionReport,
      InspectionReportExport,
      NewFeature,
      PageTitle,
      Level,
      Loader,
      ManageIssuesModal,
      ConfirmationModal,
      ActivityModal,
      FallingConfetti,
      UpgradeAd,
      Button,
      Tooltip,
      ApproveForm,
    },
    props: ['id', 'showConfetti'],
    setup(props) {
      if (!hasFeature('inspections')) {
        return renderResourceTeaserPage(t('common.inspections'));
      }

      if (!props.id) {
        return renderNotFoundPage;
      }

      const canSeeApprovals = () => {
        return approvalsEnabledForOrganization.value
          && hasSomePermission(inspectionApprovalsPermissions);
      };

      const categories = ref([] as SelectOptions);
      const isManageIssueModalOpen = ref(false);
      const isModalOpen = ref(false);
      const isResetApprovalModalOpen = ref(false);
      const record = ref<legacyRecord>({
        inspection_id: null,
        pdf_url: null,
        photos_url: null,
        activities_count: 0,
        authorized_to_edit: false,
        status: null,
        fullTranslationLanguages: [],
      });
      const issues = ref<IssueRecord[]>([]);

      const openModal = () => {
        isModalOpen.value = true;
      };

      const closeModal = () => {
        isModalOpen.value = false;
      };

      const closeManageIssueModal = () => {
        isManageIssueModalOpen.value = false;
      };

      const openResetApprovalModal = () => {
        isResetApprovalModalOpen.value = true;
      };

      const closeResetApprovalModal = () => {
        isResetApprovalModalOpen.value = false;
      };

      const getRecord = useTask(
        () => sequence(
          get(props.id),
          getAllCategories(),
        ),
        ([data, categoriesResponse]) => {
          record.value = data.parsed as legacyRecord;
          categories.value = filterListToOptions(categoriesResponse);
        },
      );

      const showTranslations = computed(() => {
        return isFormTranslationsFeatureEnabled.value
          && !!user.value.formTranslationsLocale
          && !!record.value.fullTranslationLanguages?.length;
      });

      const isSystemRejected = computed(() => record.value.status === Status.SystemRejected);

      const canSeeIssueButton = computed(
        () => {
          return hasSomePermission([Permissions.MANAGE_ALL_ISSUE, Permissions.MANAGE_OWN_ISSUE])
            && !isSystemRejected.value;
        },
      );

      const canSeeEditButton = computed(
        () => {
          const hasEditPermission = hasPermission(Permissions.EDIT_ALL_INSPECTION)
            || (hasPermission(Permissions.EDIT_OWN_INSPECTION) && record.value.authorized_to_edit);

          if (!hasEditPermission) {
            return false;
          }

          if (isSystemRejected.value && !record.value.is_conflict_copy) {
            return false;
          }

          return true;
        },
      );

      const getIssues = useTask(
        () => listInspectionIssues(props.id),
        response => {
          issues.value = response.data.filter(issue =>
            issue.data.type === IssueType.AutomaticForm
            || issue.data.type === IssueType.ManualForm
            || issue.data.type === IssueType.Automatic);
        },
      );

      const mergeAttributesIssueCreation = computed(() => ({
        inspection: props.id,
        type: IssueType.ManualForm,
      }));

      const onIssueCreated = () => {
        isManageIssueModalOpen.value = false;
        canSeeIssueButton.value && getIssues.run();
      };

      const onAddIssueClick = () => {
        isManageIssueModalOpen.value = true;
        EventBus.$emit(Events.viewedNewFeature, FeatureNames.ADD_ACTION_TO_INSPECTION);
      };

      const { approvalStatusToTagType } = useApproval();
      const isApprovalFormVisible = computed(() => record.value.approval?.status === ApprovalStatus.Pending && canSeeApprovals());

      const canResetApproval = computed(() => record.value.approval?.status !== ApprovalStatus.Pending);
      const resetApproval = async () => {
        if (record.value.approval?.id) {
          isResetApprovalModalOpen.value = false;

          await resetApprovalAction(record.value.approval.id)();
          await getRecord.run();
        }
      };

      watch(ref(props.id), getRecord.run);

      return {
        t,
        isOnFreePlan,
        categories,
        isModalOpen,
        isManageIssueModalOpen,
        record,
        issues,
        canSeeIssueButton,
        canSeeEditButton,
        mergeAttributesIssueCreation,
        getRecord,
        getIssues,
        openModal,
        closeModal,
        closeResetApprovalModal,
        closeManageIssueModal,
        onIssueCreated,
        onAddIssueClick,
        trackClick,
        CLICK_EVENTS,
        FeatureNames,
        approvalStatusToTagType,
        approvalStatusToText,
        reloadInspection: getRecord.run,
        isApprovalFormVisible,
        resetApproval,
        openResetApprovalModal,
        isResetApprovalModalOpen,
        canResetApproval,
        isSystemRejected,
        reportLowQualityImagesEnabled: user.value.organization.reportLowQualityImagesEnabled,
        showTranslations,
      };
    },
  });
